import { create } from 'zustand';
import { AXES_LANDMARK_IDS } from '../lib/alignmentLandmarks';
import {
  clearBkAlignmentLandmarks,
  loadBkAlignmentLandmarks,
  persistBkAlignmentLandmarks,
} from '../lib/bkAlignmentLandmarksStorage';

const ids = AXES_LANDMARK_IDS;

const useAxesAlignmentStore = create((set, get) => ({
  pendingAlignmentApply: false,
  requestApplyAlignment: () => set({ pendingAlignmentApply: true }),
  clearPendingAlignmentApply: () => set({ pendingAlignmentApply: false }),

  pendingAlignmentUndo: false,
  requestUndoAlignment: () => set({ pendingAlignmentUndo: true }),
  clearPendingAlignmentUndo: () => set({ pendingAlignmentUndo: false }),

  /** Serialized THREE.Matrix4 elements to reverse the last baked alignment */
  alignmentBakeSnapshot: null,
  setAlignmentBakeSnapshot: (snapshot) => set({ alignmentBakeSnapshot: snapshot }),

  /**
   * Landmark positions in shaded-mesh local space (post-alignment bake).
   * Used after spheres are removed; survives main process (e.g. locking step 7).
   * @type {import('../lib/alignmentLandmarks').LandmarkPositionsMap | null}
   */
  landmarkPositions: null,
  setLandmarkPositions: (landmarkPositions) => set({ landmarkPositions }),
  restoreLandmarkPositionsFromBkStorage: () => {
    const positions = loadBkAlignmentLandmarks();
    if (positions) set({ landmarkPositions: positions });
    return positions;
  },
  persistLandmarkPositionsToBkStorage: () => {
    const { landmarkPositions } = get();
    if (landmarkPositions) persistBkAlignmentLandmarks(landmarkPositions);
  },

  // Stores each point selection with sphere mesh, and completion flag
  selections: {
    subPatellar:      { sphere: null, completed: false },
    midPatellar:      { sphere: null, completed: false },
    frontEnd:         { sphere: null, completed: false },
    internalCondyle:  { sphere: null, completed: false },
    externalCondyle:  { sphere: null, completed: false },
  },

  // Which key is currently active for placement (ignored if already completed)
  activeKey: null,

  // Activate a selection key (prepares for raycasting)
  setActiveKey: (key) => {
    const { selections } = get();
    if (key && selections[key].completed) return;  // skip completed
    set({ activeKey: key });
  },

  // Save the clicked point and sphere, mark completed, then clear activeKey
  savePoint: (key, sphere) => {
    set((state) => ({
      selections: {
        ...state.selections,
        [key]: { sphere, completed: true },
      },
    }));

    // Find the index of the current key
    const currentIndex = ids.indexOf(key);

    // Find the next available ID that is not yet completed
    let nextIndex = -1;
    for (let i = currentIndex + 1; i < ids.length; i++) {
      if (!get().selections[ids[i]].completed) {
        nextIndex = i;
        break;
      }
    }

    // If a next key exists, set it as activeKey
    if (nextIndex !== -1) {
      set({ activeKey: ids[nextIndex] });
    } else {
      set({ activeKey: null });
    }
  },

  // Reset a single selection
  resetPoint: (key) => {
    const mesh = get().selections[key].sphere;
    if (mesh) {
      mesh.parent?.remove(mesh);
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    }
    
    // reset the selection state
    set((state) => ({
      selections: {
        ...state.selections,
        [key]: { sphere: null, completed: false },
      },
      isAligned: false,
      pendingAlignmentApply: false,
      pendingAlignmentUndo: false,
      alignmentBakeSnapshot: null,
      landmarkPositions: null,
    }));
    clearBkAlignmentLandmarks();
  },

  /** Remove placed sphere meshes after a successful bake; keeps completed flags and alignment state */
  removeLandmarkSpheresAfterBake: () => {
    const { selections } = get();
    for (const id of ids) {
      const mesh = selections[id]?.sphere;
      if (mesh) {
        mesh.parent?.remove(mesh);
        mesh.geometry?.dispose();
        mesh.material?.dispose();
      }
    }
    set((state) => ({
      selections: Object.fromEntries(
        ids.map((id) => [id, { sphere: null, completed: state.selections[id]?.completed ?? false }]),
      ),
      activeKey: null,
    }));
  },

  // Clear all selections and activeKey
  clearSelections: () => {
    const { selections } = get();
    for (const id of ids) {
      const mesh = selections[id]?.sphere;
      if (mesh) {
        mesh.parent?.remove(mesh);
        mesh.geometry?.dispose();
        mesh.material?.dispose();
      }
    }
    set({
      selections: {
        subPatellar:      { sphere: null, completed: false },
        midPatellar:      { sphere: null, completed: false },
        frontEnd:         { sphere: null, completed: false },
        internalCondyle:  { sphere: null, completed: false },
        externalCondyle:  { sphere: null, completed: false },
      },
      activeKey: null,
      isAligned: false,
      pendingAlignmentApply: false,
      pendingAlignmentUndo: false,
      alignmentBakeSnapshot: null,
      landmarkPositions: null,
    });
    clearBkAlignmentLandmarks();
  },

  // Selector to check if all selections are completed
  areAllSelectionsCompleted: () => {
    const { selections } = get();
    return Object.values(selections).every(selection => selection.completed);
  },

  isAligned: false,
  setAlignmentStatus: (status) => {
    if (!status) clearBkAlignmentLandmarks();
    set(
      status
        ? { isAligned: true }
        : {
            isAligned: false,
            alignmentBakeSnapshot: null,
            pendingAlignmentUndo: false,
            landmarkPositions: null,
          },
    );
  },
}));

export default useAxesAlignmentStore;
